import { useMemo } from 'react'
import { usePrice } from '@/lib/api/prices'
import { useVariantDetails } from '@/lib/api/variants'
import { SearchProductOutputItem, useSearchProducts } from '@/lib/api/products'
import { resolveIdSelection, ResolvedSelection, ResourceIdQuery } from './resource-id'

export type ResourceIdLookupStatus = 'idle' | 'loading' | 'found' | 'not-found'

interface UseResourceIdLookupArgs {
    storeId: string
    query: ResourceIdQuery | null
    includeInactive: boolean
}

interface ResourceIdLookup {
    status: ResourceIdLookupStatus
    selection: ResolvedSelection | null
}

/**
 * Resolve a pasted product / variant / price id to the product it belongs to.
 *
 * A price or variant id is only used to learn *which* product to load — the
 * product itself always comes from `products.search`, the same call the picker
 * already makes on every keystroke. `products.getByPriceId` / `getByVariantId`
 * look like the direct route, but nothing else in the app calls them and they
 * resolve nothing here; `prices.getById` and `variants.getById` are the ones the
 * product screens rely on.
 */
export const useResourceIdLookup = ({
    storeId,
    query,
    includeInactive,
}: UseResourceIdLookupArgs): ResourceIdLookup => {
    const isPrice = query?.kind === 'price'
    const isVariant = query?.kind === 'variant'

    const priceLookup = usePrice(isPrice ? query.id : '', isPrice)
    const variantLookup = useVariantDetails(isVariant ? query.id : '', isVariant)

    const ownerLookup = isPrice ? priceLookup : isVariant ? variantLookup : null
    const productId = query
        ? query.kind === 'product'
            ? query.id
            : (ownerLookup?.data?.productId ?? null)
        : null

    const productLookup = useSearchProducts(
        { storeId, productId, searchValue: null, includeInactive },
        { enabled: !!productId },
    )

    const isResolvingOwner = !!ownerLookup?.isLoading
    const ownerFailed = !!ownerLookup?.isError
    const product = productLookup.data?.[0] as SearchProductOutputItem | undefined

    return useMemo(() => {
        if (!query) return { status: 'idle', selection: null }
        if (isResolvingOwner || (!!productId && productLookup.isLoading)) {
            return { status: 'loading', selection: null }
        }

        // A missing id rejects at the API, which surfaces here as an error.
        const selection =
            ownerFailed || !productId || productLookup.isError
                ? null
                : resolveIdSelection({ query, product, storeId, includeInactive })

        return selection ? { status: 'found', selection } : { status: 'not-found', selection: null }
    }, [
        query,
        isResolvingOwner,
        ownerFailed,
        productId,
        productLookup.isLoading,
        productLookup.isError,
        product,
        storeId,
        includeInactive,
    ])
}
